import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from 'app/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'app/components/ui/sheet'
import { SegmentedControl, type SegmentedOption } from 'app/components/action-center/SegmentedControl'
import type { DatasetItem } from 'app/components/ops-datasets/mockData'
import { TranscriptView } from 'app/components/ops-datasets/TranscriptView'
import { cn } from 'app/lib/utils'
import { itemFieldRows, type FieldRow, type FieldSide, type ItemAnalysis } from './analysis'
import { changeLabel } from './CallQueue'
import { STAGE_NAME } from './MovementTable'
import { formatCost, formatTokens } from './pricing'
import type { Stage } from './scoring'
import { formatAccuracy, formatPoints } from './stats'

type Mode = 'changes' | 'misses' | 'all'

function Cell({ side }: { side?: FieldSide }): ReactElement {
  if (!side) return <span className="italic text-minimal">not produced</span>
  return (
    <span className="flex min-w-0 items-start gap-1">
      {side.matched ? (
        <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-700" aria-label="Correct" />
      ) : (
        <XMarkIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-700" aria-label="Wrong" />
      )}
      <span className={cn(`break-words`, side.matched ? `text-default` : `font-medium text-red-700`)}>
        {side.value}
      </span>
    </span>
  )
}

function FieldTable({
  stage,
  rows,
  candidateLabel,
  baselineLabel,
}: {
  stage: Stage
  rows: FieldRow[]
  candidateLabel: string
  baselineLabel: string
}): ReactElement {
  return (
    <div className="overflow-x-auto rounded-lg border border-default bg-surface-raised">
      <table className="w-full min-w-[30rem] text-sm">
        <caption className="border-b border-subtle px-3 py-2 text-left text-caption-sm font-semibold text-default">
          {STAGE_NAME[stage]}
        </caption>
        <thead>
          <tr className="border-b border-subtle bg-surface-subtle text-caption-sm text-subtle">
            <th scope="col" className="w-[26%] px-3 py-1.5 text-left font-medium">
              Field
            </th>
            <th scope="col" className="w-[24%] px-2 py-1.5 text-left font-medium">
              Expected
            </th>
            <th scope="col" className="w-[25%] px-2 py-1.5 text-left font-medium">
              {baselineLabel}
            </th>
            <th scope="col" className="w-[25%] px-2 py-1.5 text-left font-medium">
              {candidateLabel}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.field}.${row.person ?? 0}`}
              className={cn(
                `border-b border-subtle align-top last:border-b-0`,
                row.change === `fixed` && `bg-green-50/60`,
                row.change === `broke` && `bg-red-50/60`,
              )}
            >
              <th
                scope="row"
                className={cn(
                  `border-l-[3px] border-l-transparent px-3 py-1.5 text-left font-normal text-subtle`,
                  row.change === `fixed` && `border-l-green-600`,
                  row.change === `broke` && `border-l-red-600`,
                )}
              >
                {changeLabel(row)}
                {row.change && (
                  <span
                    className={cn(
                      `ml-1.5 text-caption-sm font-medium`,
                      row.change === `fixed` ? `text-green-700` : `text-red-700`,
                    )}
                  >
                    {row.change}
                  </span>
                )}
              </th>
              <td className="break-words px-2 py-1.5 text-default">{row.goldenValue}</td>
              <td className="px-2 py-1.5">
                <Cell side={row.baseline} />
              </td>
              <td className="px-2 py-1.5">
                <Cell side={row.candidate} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * One call, golden vs baseline vs candidate, field by field. J / K (or the arrows) step
 * through the same filtered queue the call list shows, for reviewing regressions in order.
 */
export function CallComparisonSheet({
  entry,
  stages,
  items,
  position,
  count,
  source,
  candidateLabel,
  baselineLabel,
  onStep,
  onClose,
}: {
  entry: ItemAnalysis | null
  stages: Stage[]
  /** Calls in the run, for the precision of percentages. */
  items: number
  position: number
  count: number
  source?: DatasetItem
  candidateLabel: string
  baselineLabel: string
  onStep: (offset: 1 | -1) => void
  onClose: () => void
}): ReactElement {
  const rows = useMemo(() => (entry ? itemFieldRows(entry.result, stages) : []), [entry, stages])
  const changes = rows.filter((row) => row.change).length
  const misses = rows.filter((row) => row.candidate?.matched !== true).length
  const [mode, setMode] = useState<Mode>(`changes`)
  const goldenNames = useMemo(() => source?.golden.people.map((person) => person.name) ?? [], [source])

  // Each call opens on the most useful view: what flipped, else what is still wrong.
  useEffect(() => {
    setMode(changes > 0 ? `changes` : misses > 0 ? `misses` : `all`)
  }, [entry?.result.itemId, changes, misses])

  useEffect(() => {
    if (!entry) return
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.closest(`input, textarea, [contenteditable=true]`)) return
      if (event.key === `j` || event.key === `ArrowDown`) onStep(1)
      if (event.key === `k` || event.key === `ArrowUp`) onStep(-1)
    }
    window.addEventListener(`keydown`, onKey)
    return () => window.removeEventListener(`keydown`, onKey)
  }, [entry, onStep])

  const shown = rows.filter((row) =>
    mode === `changes` ? row.change : mode === `misses` ? row.candidate?.matched !== true : true,
  )
  const options: SegmentedOption<Mode>[] = [
    { id: `changes`, label: `Changes ${changes}`, title: `Fields that flipped between the two prompts` },
    { id: `misses`, label: `Misses ${misses}`, title: `Fields the candidate got wrong` },
    { id: `all`, label: `All ${rows.length}` },
  ]

  return (
    <Sheet open={entry != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto p-4 sm:max-w-2xl sm:p-6">
        {entry && (
          <>
            <SheetHeader className="p-0 pr-8">
              <SheetTitle>{entry.result.callerName}</SheetTitle>
              <SheetDescription className="tabular-nums">
                {baselineLabel} {formatAccuracy(entry.result.baselineScore.overall, items)} → {candidateLabel}
                {` `}
                {formatAccuracy(entry.result.candidateScore.overall, items)}
                {entry.delta !== 0 && ` (${formatPoints(entry.delta, items)} pts)`} · {entry.result.model}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <SegmentedControl options={options} value={mode} onSelect={setMode} />
              <div className="flex items-center gap-1">
                <Button
                  variant="tertiary"
                  size="xs"
                  onClick={() => onStep(-1)}
                  disabled={position <= 1}
                  aria-label="Previous call"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                <span className="text-caption-sm tabular-nums text-subtle">
                  {position} of {count}
                </span>
                <Button
                  variant="tertiary"
                  size="xs"
                  onClick={() => onStep(1)}
                  disabled={position >= count}
                  aria-label="Next call"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
                <kbd className="ml-1 hidden rounded border border-default px-1 text-caption-sm text-minimal sm:inline">
                  J / K
                </kbd>
              </div>
            </div>

            <div className="mt-3 space-y-3">
              {shown.length === 0 ? (
                <p className="rounded-lg border border-green-200 bg-green-50 p-3 text-center text-sm text-green-800">
                  {mode === `changes` ? `No field flipped on this call.` : `Every field matches the golden.`}
                </p>
              ) : (
                stages
                  .filter((stage) => shown.some((row) => row.stage === stage))
                  .map((stage) => (
                    <FieldTable
                      key={stage}
                      stage={stage}
                      rows={shown.filter((row) => row.stage === stage)}
                      candidateLabel={candidateLabel}
                      baselineLabel={baselineLabel}
                    />
                  ))
              )}
            </div>

            {(entry.result.candidateText?.summary || entry.result.baselineText?.summary) && (
              <section className="mt-6">
                <h4 className="mb-2 text-sm font-semibold text-strong">
                  Summary and action items <span className="font-normal text-subtle">· not scored</span>
                </h4>
                <dl className="grid gap-3 text-sm sm:grid-cols-2">
                  {[
                    { label: candidateLabel, text: entry.result.candidateText },
                    { label: baselineLabel, text: entry.result.baselineText },
                  ].map(({ label, text }) => (
                    <div key={label} className="rounded-md border border-default p-3">
                      <dt className="mb-1 text-caption-sm font-medium text-subtle">{label}</dt>
                      <dd className="text-default">{text?.summary || `—`}</dd>
                      {text?.actionItems && <dd className="mt-2 text-subtle">Action items: {text.actionItems}</dd>}
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {source && (
              <div className="mt-6">
                <TranscriptView text={source.transcriptSnapshot} names={goldenNames} />
              </div>
            )}

            <p className="mt-4 text-caption-sm tabular-nums text-minimal">
              {candidateLabel}:{` `}
              {formatTokens(entry.result.tokens.promptTokens + entry.result.tokens.completionTokens)} tokens ·{` `}
              {formatCost(entry.result.cost)} · {(entry.result.latencyMs / 1000).toFixed(1)}s · {baselineLabel}:{` `}
              {formatTokens(entry.result.baseline.tokens.promptTokens + entry.result.baseline.tokens.completionTokens)}
              {` `}
              tokens · {formatCost(entry.result.baseline.cost)} · {(entry.result.baseline.latencyMs / 1000).toFixed(1)}s
            </p>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
